import React, { useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { SignupV2Presenter } from './signup-v2-presenter'
import { signUpV2Schema } from '../../lib/utils/schema'
import { projectServices, userServices } from '../../services'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { createConfiguration } from '../../global-state/investor-slice'
import ReCAPTCHA from 'react-google-recaptcha'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import posthog from '../../lib/utils/posthogUtils/posthog'
import { EVENTS } from '../../lib/utils/constants'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { Role, IFormProps } from '../../lib/types'
import { CBLogoV2 } from '../../lib/components/Icon'
import { trackGaSignUp } from '../../lib/analytics/google-analytics'
import {
  clearStoredUtmAttribution,
  getStoredUtmAttribution,
  parseUtmFromSearchParams,
  toUtmAttributionPayload,
  toUtmEventProperties,
} from '../../lib/utils/utm-attribution'
import {
  resolveCampaignOpportunitySlug,
  storeCampaignSignupRedirect,
} from '../../lib/utils/campaign-signup-redirect'
import { resolvePostLoginDestination } from '../../lib/utils/post-login'
import './signup-v2.scss'

const initialValues = {
  name: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  referralCode: '',
  termsAccepted: false,
}

const SignupV2Container = () => {
  const reRef = useRef<ReCAPTCHA>(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { email: signupParam } = useParams<{ email?: string }>()
  const isEmailPrefill = Boolean(signupParam?.includes('@'))
  const opportunitySlug = useMemo(
    () => resolveCampaignOpportunitySlug(signupParam, location.search),
    [signupParam, location.search],
  )
  const campaignReferralCode = useMemo(
    () =>
      parseUtmFromSearchParams(location.search).utm_source ??
      getStoredUtmAttribution()?.utm_source ??
      '',
    [location.search],
  )
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null)
  const [heroImageMobileUrl, setHeroImageMobileUrl] = useState<string | null>(null)
  const [heroAriaLabel, setHeroAriaLabel] = useState('Professional working on a laptop')
  const [landingResolved, setLandingResolved] = useState(false)

  useEffect(() => {
    if (!opportunitySlug) {
      setHeroImageUrl(null)
      setHeroImageMobileUrl(null)
      setHeroAriaLabel('Professional working on a laptop')
      setLandingResolved(false)
      return
    }

    let isCancelled = false
    setLandingResolved(false)

    const loadMarketingSignupLanding = async () => {
      const response = await projectServices.getMarketingSignupLanding(opportunitySlug)

      if (isCancelled) return

      if (response?.status === 200 && response.data?.hero_image && response.data.opportunity_id) {
        setHeroImageUrl(response.data.hero_image)
        setHeroImageMobileUrl(response.data.hero_image_mobile ?? null)
        setHeroAriaLabel(
          response.data.opportunity_title
            ? `${response.data.opportunity_title} featured image`
            : 'Featured opportunity image',
        )
        storeCampaignSignupRedirect({
          opportunity_slug: response.data.opportunity_slug,
          opportunity_title: response.data.opportunity_title,
          opportunity_id: response.data.opportunity_id,
          status: response.data.status,
          access_token: response.data.access_token,
        })
        setLandingResolved(true)
        return
      }

      setHeroImageUrl(null)
      setHeroImageMobileUrl(null)
      setHeroAriaLabel('Professional working on a laptop')
      setLandingResolved(true)
    }

    void loadMarketingSignupLanding()

    return () => {
      isCancelled = true
    }
  }, [opportunitySlug])

  const isCustomizedSignup = Boolean(heroImageUrl)
  const showBrandPromo = !heroImageUrl && (!opportunitySlug || landingResolved)

  const handleSubmit_v1 = async (val: {
    name?: string
    email?: string
    password?: string
    passwordConfirmation?: string
    referralCode?: string
    termsAccepted?: boolean
  }) => {
    if (!val.termsAccepted) return
    const token = await reRef.current?.executeAsync()
    reRef.current?.reset()
    const utmAttribution = toUtmAttributionPayload(getStoredUtmAttribution())
    const createUser = await userServices.userSignUp_v1(
      {
        name: val.name ?? '',
        email: (val.email ?? '').toLowerCase(),
        password: val.password ?? '',
        role: Role.investor,
        referralCode: val.referralCode || undefined,
        utmAttribution,
      },
      { 'g-recaptcha-response': token ? token : '' },
    )

    const isUserCreationSuccessful =
      !!createUser && createUser.status === 200 && createUser?.data?.role === Role.investor

    if (isUserCreationSuccessful) {
      const createdUser = createUser.data as typeof createUser.data & {
        userId?: number
        id?: number
      }

      dispatch(
        createConfiguration({
          ...createdUser,
          userId: createdUser.userId ?? createdUser.id,
        }),
      )
      dispatch(
        showBanner({
          text: 'Please verify your email address',
          variant: 'warning-banner',
        }),
      )

      logCrmEvent(
        EngagementEvents.USER_SIGNED_UP_EMAIL,
        { Authorization: createUser.data.authToken ?? '' },
        { name: createUser.data.name, email: createUser.data.email },
      )

      const utmEventProperties = toUtmEventProperties(getStoredUtmAttribution())
      const signupEmail = (val.email ?? '').toLowerCase()
      captureEvent(EVENTS.user_signed_up, {
        email: signupEmail,
        ...utmEventProperties,
      })
      posthog.identify(signupEmail, utmEventProperties)
      trackGaSignUp('email', utmEventProperties)
      clearStoredUtmAttribution()

      navigate('/verify-account', {
        state: {
          email: createdUser.email ?? (val.email ?? '').toLowerCase(),
          investorId: createdUser.userId ?? createdUser.id,
        },
      })
    } else if (
      !!createUser &&
      createUser.status === 200 &&
      createUser?.data?.role !== Role.investor
    ) {
      dispatch(showBanner({ text: 'Not allowed to sign up!', variant: 'error-banner' }))
    } else {
      dispatch(
        showBanner({
          text:
            (createUser?.data && createUser.data.message) ||
            'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  const googleLoginHandler = async (res: object, referralCode?: string) => {
    const { access_token } = res as { access_token: string }
    const utmAttribution = toUtmAttributionPayload(getStoredUtmAttribution())
    const trimmedReferral = referralCode?.trim()
    const user = await userServices.googleSignIn_v1({
      accessToken: access_token,
      role: Role.investor,
      ...(trimmedReferral ? { referralCode: trimmedReferral } : {}),
      ...(utmAttribution ? { utmAttribution } : {}),
    })
    if (!!user && user.status === 200 && user?.data?.role === Role.investor) {
      const isEmailVerified = user.data.isEmailVerified ?? user.data.is_verified ?? false
      const acceptedNDA = user.data.acceptedNDA ?? false

      if (isEmailVerified) {
        const session = await resolvePostLoginDestination(
          user.data.authToken,
          user.data.userId,
          user.data.email,
          user.data.name,
          acceptedNDA,
        )

        flushSync(() => {
          navigate(session.destination)
        })

        dispatch(
          createConfiguration({
            ...user.data,
            accountStatus: user.data.account_status,
            isEmailVerified,
            acceptedNDA,
            kycStatus: user.data.kycStatus ?? '',
            basicDetailsCompleted: session.basicDetailsCompleted,
            riskProfileCompleted: session.riskProfileCompleted,
            riskProfilerRequired: session.riskProfilerRequired,
          }),
        )
      } else {
        dispatch(
          createConfiguration({
            ...user.data,
            accountStatus: user.data.account_status,
            isEmailVerified,
            acceptedNDA,
            kycStatus: user.data.kycStatus ?? '',
          }),
        )
      }

      dispatch(showBanner({ text: 'Login Success', variant: 'success-banner' }))

      const isNewUser = user.data.isNewUser === true
      const utmEventProperties = toUtmEventProperties(getStoredUtmAttribution())

      if (isNewUser) {
        logCrmEvent(
          EngagementEvents.USER_SIGNED_UP_SOCIAL,
          { Authorization: user.data.authToken ?? '' },
          { name: user.data.name, email: user.data.email },
        )
        captureEvent(EVENTS.user_signed_up, {
          email: user.data.email,
          ...utmEventProperties,
        })
        posthog.identify(user.data.email, utmEventProperties)
        trackGaSignUp('google', utmEventProperties)
        clearStoredUtmAttribution()
      } else {
        logCrmEvent(
          EngagementEvents.USER_LOGGED_IN_SOCIAL,
          { Authorization: user.data.authToken ?? '' },
          { email: user.data.email, socialPlatform: 'google' },
        )
        posthog.identify(user.data.email)
      }
    } else if (!!user && user.status === 200 && user?.data?.role !== Role.investor) {
      dispatch(showBanner({ text: 'Not allowed to sign up!', variant: 'error-banner' }))
    } else {
      dispatch(
        showBanner({
          text:
            (user?.data && (user.data as { message?: string }).message) ||
            'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <div className='sign-up-page' data-testid='sign-up-page'>
      <aside
        className={`sign-up-page__brand${
          isCustomizedSignup ? ' sign-up-page__brand--campaign' : ''
        }`}
        aria-label={isCustomizedSignup ? 'Opportunity branding' : 'Credibila value proposition'}
      >
        {showBrandPromo && (
          <header className='sign-up-page__brand-header'>
            <p className='sign-up-page__brand-header-text'>
              Unlock Access to Private Equity &amp; other alternative assets with a low minimum
            </p>
          </header>
        )}
        <div
          className={
            heroImageUrl
              ? 'sign-up-page__brand-hero sign-up-page__brand-hero--campaign'
              : 'sign-up-page__brand-hero'
          }
          role='img'
          aria-label={heroAriaLabel}
          style={
            heroImageUrl
              ? ({
                  '--hero-desktop': `url(${heroImageUrl})`,
                  ...(heroImageMobileUrl ? { '--hero-mobile': `url(${heroImageMobileUrl})` } : {}),
                } as React.CSSProperties)
              : undefined
          }
        />
        {showBrandPromo && (
          <section
            className='sign-up-page__brand-stats'
            aria-label='Key statistics'
            data-testid='signup-hero-stats-banner'
          >
            <div className='sign-up-page__stat-block'>
              <div className='sign-up-page__stat-value'>6+ Countries</div>
              <div className='sign-up-page__stat-label'>Global Presence</div>
            </div>
            <div className='sign-up-page__stat-block'>
              <div className='sign-up-page__stat-value'>3000+</div>
              <div className='sign-up-page__stat-label'>Strong Community</div>
            </div>
            <div className='sign-up-page__stat-block'>
              <div className='sign-up-page__stat-value'>80+ Years</div>
              <div className='sign-up-page__stat-label'>Combined Team Experience</div>
            </div>
            <div className='sign-up-page__stat-block'>
              <div className='sign-up-page__stat-value'>$25+ Million</div>
              <div className='sign-up-page__stat-label'>AUM</div>
            </div>
          </section>
        )}
      </aside>
      <main className='sign-up-page__form-section'>
        <div className='sign-up-page__form-card'>
          <Link to='/home' className='sign-up-page__form-logo' aria-label='Go to home'>
            <CBLogoV2 classes={['sign-up-page__form-logo-svg']} width='168' height='40' />
          </Link>
          <SignupV2Presenter
            initialValues={
              {
                ...initialValues,
                email: isEmailPrefill ? signupParam || '' : '',
                referralCode: campaignReferralCode,
              } as unknown as IFormProps['initialValues']
            }
            validationSchema={signUpV2Schema}
            handleFormSubmit={handleSubmit_v1}
            reRef={reRef}
            googleLoginHandler={googleLoginHandler}
            isReferralCodeLocked={Boolean(campaignReferralCode)}
          />
        </div>
      </main>
    </div>
  )
}

export { SignupV2Container }
